// Inbox › Calls in Blueprint (the approved page story, Pages/Inbox/Calls): what its lists say, kept out
// of the component so it's tested without a browser. The day's line under the title; On calls now (who
// is on each live call, who has it, how long); To call back (each one's kind and time, what happened,
// the line it came in on, the number, and its one action); Earlier today (how each went as its kicker,
// who took it and how long). The server decides what each call is; this only words it, and says
// nothing it doesn't know: a call the phone system named nobody on shows no one. Reason and job type
// names are the company's, said as written.
import { phoneText, type Call } from '../types.ts';
import { laterWord, WHY, type Callback } from './callbacks-model.ts';
import { campaignWord } from './source-model.ts';

/**
 * Who's calling, as a list names them: the customer the number is ("Lakeside Dental"), "One of 2
 * customers" when it's several, else "Unknown caller". The strip names the person; a list, as the story
 * has it, the customer.
 */
export function listName(c: Pick<Call, 'matches'>): string {
  if (c.matches.length > 1) return `One of ${c.matches.length} customers`;
  return c.matches[0]?.customerName || 'Unknown caller';
}

/** How many of Earlier today show before "Show all". */
export const EARLIER_SHOWN = 5;

/** "Today: 14 calls, 10 booked." */
export function daySummary(total: number, booked: number): string {
  if (!total) return 'Today: no calls yet.';
  return `Today: ${total} ${total === 1 ? 'call' : 'calls'}, ${booked} booked.`;
}

/** A call's length: "4 min", "under a minute". */
export function lengthWord(seconds: number): string {
  return seconds < 60 ? 'under a minute' : `${Math.round(seconds / 60)} min`;
}

/** How long a missed call rang: "30 s", "3 min". */
export const rangWord = (seconds: number) =>
  seconds < 120 ? `${seconds} s` : `${Math.round(seconds / 60)} min`;

/** A voicemail's length as a player shows it: "0:41", "2:05". */
export const clockWord = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

/** A voicemail's words, cut at a word near `max` so a row stays a row. */
export function clip(words: string, max = 160): string {
  const t = words.replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s,.;:]+$/, '')}…`;
}

/**
 * Who took it: the phone system's own name for who answered, else who took it here. Null when nobody
 * was named: a forwarded call only knows the line it rang, and an import that named no one says so as
 * "Not recorded", which isn't a person.
 */
export function whoTook(c: Pick<Call, 'answeredBy' | 'takenBy'>): string | null {
  if (c.answeredBy) return c.answeredBy;
  return c.takenBy && c.takenBy !== 'Not recorded' ? c.takenBy : null;
}

/** Earlier today, one call: how it went (the kicker over the name) and its mark. */
export function earlierState(c: Call): { kind: 'done' | 'callback' | 'missed'; state: string } {
  if (c.outcome === 'booked') {
    const job = c.job;
    if (!job) return { kind: 'done', state: 'Booked' };
    return {
      kind: 'done',
      state: job.type ? `Booked: ${job.type}, job #${job.number}` : `Booked: job #${job.number}`,
    };
  }
  if (c.reason)
    return {
      kind: 'done',
      state: `${c.reason.isLead ? 'Not booked' : 'Not a lead'}: ${c.reason.name}`,
    };
  // a reason the call suggested, waiting for whoever took it to say yes
  if (c.suggestedReasonId) return { kind: 'callback', state: 'To confirm: why they called' };
  if (c.outcome === 'not_booked') return { kind: 'callback', state: 'Not booked: no reason yet' };
  if (c.handledOn)
    return {
      kind: 'done',
      state: `${c.status === 'voicemail' ? 'Voicemail' : 'Missed'}: handled on a later call`,
    };
  if (c.status === 'missed') return { kind: 'missed', state: 'Missed' };
  if (c.status === 'voicemail') return { kind: 'callback', state: 'Voicemail' };
  if (c.status === 'ringing') return { kind: 'done', state: 'Rang' };
  return { kind: 'done', state: 'Call ended' };
}

/** Earlier today, beside the name: who took it, and how long it ran ("Ben T. · 4 min"). */
export function earlierWho(c: Call): string {
  const length =
    c.status === 'completed' && c.durationSeconds !== null && c.durationSeconds !== undefined
      ? lengthWord(c.durationSeconds)
      : null;
  return [whoTook(c), length].filter(Boolean).join(' · ');
}

/**
 * The line a call came in on (its list's "Came in on"): in the company's words, its tracking number's
 * label, else its campaign; else the number they dialled (QA-773).
 */
export function lineOf(c: {
  trackingLabel?: string | null;
  campaign?: Call['campaign'];
  to?: string;
}): string | null {
  return c.trackingLabel || (c.campaign ? campaignWord(c.campaign) : null) || (c.to ? phoneText(c.to) : null);
}

/** To call back, one call: its kind and time over the name ("Missed, 10:05 a.m."). */
export const backState = (b: Callback, when: string) => `${WHY[b.why]}, ${when}`;

/** Who's calling: the customer the number is, else nobody we know. */
export const backName = (b: Callback) => b.customerName ?? 'Unknown caller';

/**
 * To call back, what happened: how long a missed one rang, a voicemail's words and length, or what
 * an answered one still needs; then who has it, a text back, and their later call.
 */
export function backWhat(b: Callback, whenOf: (iso: string) => string): string {
  const first =
    b.why === 'missed'
      ? b.ringSeconds
        ? `Rang ${rangWord(b.ringSeconds)}, nobody answered.`
        : 'Nobody answered.'
      : b.why === 'voicemail'
        ? b.voicemailWords
          ? `Voicemail${b.voicemailSeconds ? `, ${clockWord(b.voicemailSeconds)}` : ''}: “${clip(b.voicemailWords)}”`
          : b.voicemailSeconds
            ? `Voicemail, ${clockWord(b.voicemailSeconds)}.`
            : 'Left a voicemail.'
        : b.why === 'no_outcome'
          ? 'Answered; nothing recorded yet.'
          : 'Not booked; no reason yet.';
  return [
    first,
    b.takenBy ? `${b.takenBy} has it.` : null,
    // "a.m." ends its own sentence
    b.textedBackAt ? `Texted back ${whenOf(b.textedBackAt)}`.replace(/\.?$/, '.') : null,
    b.laterCall ? `${laterWord(b.laterCall, whenOf(b.laterCall.startedAt))}.` : null,
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * Its one action: Call back for a missed call or a voicemail; one tap to say a later call handled it
 * when the caller called again and that call has an outcome; an answered call that's waiting is opened
 * to record how it went.
 */
export function backAction(b: Callback): 'call-back' | 'handled' | 'open' {
  if (b.laterCall) return 'handled';
  return b.why === 'missed' || b.why === 'voicemail' ? 'call-back' : 'open';
}

/** The number, in the time column (the story's place for it). */
export const backNumber = (b: Callback) => phoneText(b.from);
